/**
 * Cognito Authentication Module
 * 
 * Configures AWS Amplify Auth to connect to the Tokyo Cognito User Pool
 * and provides authentication functions for the application.
 * 
 * Requirements: 2.5, 2.6, 2.7
 */

import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  confirmSignIn as amplifyConfirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  type SignInInput,
  type SignInOutput,
  type ConfirmSignInOutput,
} from 'aws-amplify/auth';
import type { AuthenticationLevel } from '@/types/user';

/**
 * Configure Amplify Auth
 * 
 * Points to the Tokyo Cognito User Pool since Taipei doesn't support Cognito yet.
 * Configuration values come from environment variables set during build.
 * 
 * Note: Amplify is now configured in main.ts before the app starts.
 * This function is kept for backward compatibility but does nothing
 * since configuration happens at app startup.
 */
export function configureAuth(): void {
  // Amplify is configured in main.ts
  // This function is kept for backward compatibility
  console.log('configureAuth called (Amplify already configured in main.ts)');
}

/**
 * User session information
 */
export interface UserSession {
  username: string;
  accessToken: string;
  idToken: string;
  authentication: AuthenticationLevel;
  email?: string;
  phone?: string;
  lastName?: string;
  firstName?: string;
}

/**
 * Sign in with username and password
 * 
 * @param username - The user's username
 * @param password - The user's password
 * @returns Sign in result from Cognito
 * 
 * Requirements: 2.5
 */
export async function signIn(username: string, password: string): Promise<SignInOutput> {
  const input: SignInInput = {
    username,
    password,
  };
  
  return amplifySignIn(input);
}

/**
 * Confirm sign in with new password
 * 
 * Used when user needs to set a new password (FORCE_CHANGE_PASSWORD status).
 * 
 * @param newPassword - The new password to set
 * @returns Confirm sign in result from Cognito
 * 
 * Requirements: 2.5
 */
export async function confirmSignInWithNewPassword(newPassword: string): Promise<ConfirmSignInOutput> {
  return amplifyConfirmSignIn({
    challengeResponse: newPassword,
  });
}

/**
 * Sign out the current user
 * 
 * Clears the local session and signs out from Cognito.
 * 
 * Requirements: 2.5
 */
export async function signOut(): Promise<void> {
  await amplifySignOut();
}

/**
 * Get the current authenticated user
 * 
 * @returns The current user's username or null if not authenticated
 * 
 * Requirements: 2.5
 */
export async function getCurrentUser(): Promise<string | null> {
  try {
    const user = await amplifyGetCurrentUser();
    return user.username;
  } catch {
    // User is not authenticated
    return null;
  }
}

/**
 * Get the current user's session with tokens and attributes
 * 
 * @returns User session information or null if not authenticated
 * 
 * Requirements: 2.5, 2.6
 */
export async function getUserSession(): Promise<UserSession | null> {
  try {
    const user = await amplifyGetCurrentUser();
    const session = await fetchAuthSession();
    const attributes = await fetchUserAttributes();
    
    const accessToken = session.tokens?.accessToken?.toString();
    const idToken = session.tokens?.idToken?.toString();
    
    if (!accessToken || !idToken) {
      return null;
    }
    
    // Get custom authentication attribute
    const authentication = (attributes['custom:authentication'] as AuthenticationLevel) || 'watcher';
    
    return {
      username: user.username,
      accessToken,
      idToken,
      authentication,
      email: attributes.email,
      phone: attributes['custom:phone'],
      lastName: attributes['custom:last_name'],
      firstName: attributes['custom:first_name'],
    };
  } catch {
    // User is not authenticated or session is invalid
    return null;
  }
}

/**
 * Get the current access token
 * 
 * Used by the API client to attach JWT token to requests.
 * 
 * @returns The access token string or null if not authenticated
 * 
 * Requirements: 2.6
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Refresh the current session tokens
 * 
 * Forces a token refresh from Cognito.
 * 
 * @returns The new access token or null if refresh failed
 * 
 * Requirements: 2.7
 */
export async function refreshToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.accessToken?.toString() || null;
  } catch {
    // Token refresh failed - user needs to sign in again
    return null;
  }
}

/**
 * Check if the current user is authenticated
 * 
 * @returns true if authenticated, false otherwise
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await amplifyGetCurrentUser();
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the current user's authentication level
 * 
 * @returns The authentication level or 'watcher' as default
 */
export async function getAuthenticationLevel(): Promise<AuthenticationLevel> {
  try {
    const attributes = await fetchUserAttributes();
    return (attributes['custom:authentication'] as AuthenticationLevel) || 'watcher';
  } catch {
    return 'watcher';
  }
}
